import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import SportsSoccerIcon from '@mui/icons-material/SportsSoccer';
import GroupsIcon from '@mui/icons-material/Groups';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import StarIcon from '@mui/icons-material/Star';
import StadiumIcon from '@mui/icons-material/Stadium';
import Diversity3Icon from '@mui/icons-material/Diversity3';
import SelfImprovementIcon from '@mui/icons-material/SelfImprovement';

const C = { blue: '#3f51b5', yellow: '#c4d835', dark: '#2c387e', ylight: '#d4e157' };

const card = {
    background: `linear-gradient(135deg, ${C.blue}ee, ${C.dark}ee)`,
    borderRadius: '20px',
    border: `2px solid ${C.yellow}35`,
    padding: '28px',
    boxShadow: `0 8px 32px ${C.blue}22`,
    marginBottom: '20px',
    width: '100%',
    boxSizing: 'border-box',
};

const heading = { color: C.yellow, fontWeight: 700, marginBottom: '12px' };
const body = { color: C.ylight, lineHeight: 1.85 };
const ibox = {
    width: 44, height: 44, borderRadius: '12px',
    background: `${C.yellow}28`,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0, marginBottom: '12px',
};
const heroTitle = {
    fontWeight: 800,
    fontSize: '2.4rem',
    background: `linear-gradient(45deg, ${C.yellow}, ${C.ylight})`,
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    textAlign: 'center',
};
const primaryBtn = {
    padding: '12px 32px',
    fontWeight: 700,
    borderRadius: '12px',
    background: `linear-gradient(45deg, ${C.yellow}, ${C.ylight})`,
    color: C.dark,
    textTransform: 'none',
    fontSize: '1rem',
    border: 'none',
};
const outlineBtn = {
    padding: '12px 32px',
    fontWeight: 700,
    borderRadius: '12px',
    borderColor: C.yellow,
    color: C.yellow,
    textTransform: 'none',
    fontSize: '1rem',
};

const AboutUsPage = () => {
    const navigate = useNavigate();

    return (
        <Box sx={{ maxWidth: 880, mx: 'auto', py: 2 }}>

            {/* Hero */}
            <Box sx={{ ...card, textAlign: 'center', py: 5 }}>
                <Box sx={{ ...ibox, width: 60, height: 60, borderRadius: '50%', mx: 'auto', mb: 2 }}>
                    <SportsSoccerIcon sx={{ fontSize: 32, color: C.yellow }} />
                </Box>
                <Typography variant="h1" component="h1" sx={heroTitle}>
                    THE GRID BY FC ROVERS
                </Typography>
                <Typography variant="overline" sx={{ color: C.ylight, letterSpacing: '0.18em', display: 'block', mt: 1 }}>
                    About Us
                </Typography>
            </Box>

            {/* Welcome */}
            <Box sx={card}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                    <Box sx={ibox}><SportsSoccerIcon sx={{ color: C.yellow, fontSize: 22 }} /></Box>
                    <Typography variant="h6" sx={heading}>Welcome to The Grid</Typography>
                </Box>
                <Typography sx={{ ...body, mb: 2 }}>
                    Welcome to The Grid — Karachi's premier sports, culture, and community destination, designed to bring people together through active lifestyle experiences and exciting athletic activities. Whether you're passionate about futsal, cricket, or looking for a vibrant community space to connect with others, The Grid offers a modern and dynamic environment that inspires play, performance, and social engagement.
                </Typography>
                <Typography sx={body}>
                    At The Grid, we provide state-of-the-art courts and facilities that serve not only as a place to play sport but as a hub for fitness, fun, and local culture. Our courts are open for premium futsal and cricket bookings, attracting players from all over Karachi who seek quality playing surfaces, professional surroundings, and a welcoming atmosphere. Bookings can be made easily through our online platform, making it convenient for athletes, teams, and community members to enjoy their game on their schedule.
                </Typography>
            </Box>

            {/* Vision + Mission side by side */}
            <Box sx={{ display: 'flex', gap: '20px', mb: '20px', flexDirection: { xs: 'column', sm: 'row' } }}>
                <Box sx={{ ...card, mb: 0, flex: 1, borderLeft: `4px solid ${C.yellow}` }}>
                    <Box sx={ibox}><EmojiEventsIcon sx={{ color: C.yellow, fontSize: 22 }} /></Box>
                    <Typography variant="h6" sx={heading}>Our Vision</Typography>
                    <Typography variant="body2" sx={body}>
                        To be Karachi's most inclusive and exciting sports destination — where athletic ambition meets community spirit and friendships are formed through shared experiences on and off the court.
                    </Typography>
                </Box>
                <Box sx={{ ...card, mb: 0, flex: 1 }}>
                    <Box sx={ibox}><StarIcon sx={{ color: C.yellow, fontSize: 22 }} /></Box>
                    <Typography variant="h6" sx={heading}>Our Mission</Typography>
                    <Box component="ul" sx={{ m: 0, pl: 2.5, color: C.ylight, lineHeight: 2.1 }}>
                        <li>Provide high-quality sports facilities that empower players of all levels.</li>
                        <li>Encourage a healthy, active lifestyle within the community.</li>
                        <li>Foster a culture of teamwork, skill development, and sportsmanship.</li>
                    </Box>
                </Box>
            </Box>

            {/* FC Rovers */}
            <Box sx={{ ...card, borderLeft: `4px solid ${C.yellow}` }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                    <Box sx={ibox}><GroupsIcon sx={{ color: C.yellow, fontSize: 22 }} /></Box>
                    <Box>
                        <Typography variant="h6" sx={{ ...heading, mb: 0 }}>FC Rovers Karachi Malir Cantt</Typography>
                        <Typography variant="caption" sx={{ color: C.ylight, opacity: 0.85 }}>Our Sporting Partner</Typography>
                    </Box>
                </Box>
                <Typography variant="body2" sx={{ ...body, mb: 2 }}>
                    At the heart of The Grid's community connection is our strong relationship with FC Rovers Karachi Malir Cantt, a dedicated local football club based in Malir Cantonment, Karachi. Founded in 2003 and officially registered with the District Football Association (DFA) Malir in 2007, FC Rovers has grown into a respected club that nurtures local football talent and promotes the sport across the region.
                </Typography>
                <Typography variant="body2" sx={body}>
                    FC Rovers is known for its commitment to youth development, community engagement, and spirited competition in local football leagues. Together with The Grid, we aim to create pathways where young players and sports enthusiasts can train, compete, and excel, helping build a stronger sporting culture in Karachi.
                </Typography>
            </Box>

            {/* Why Choose - 2x2 grid */}
            <Typography variant="h6" sx={{ ...heading, textAlign: 'center', mb: 2 }}>
                Why Choose The Grid + FC Rovers?
            </Typography>
            <Box sx={{ display: 'flex', gap: '20px', mb: '20px', flexDirection: { xs: 'column', sm: 'row' } }}>
                <Box sx={{ ...card, mb: 0, flex: 1 }}>
                    <Box sx={ibox}><StadiumIcon sx={{ color: C.yellow, fontSize: 22 }} /></Box>
                    <Typography variant="subtitle2" sx={{ color: C.yellow, fontWeight: 700, mb: 0.5 }}>Professional Facilities</Typography>
                    <Typography variant="body2" sx={body}>Premium futsal and cricket courts with easy online booking.</Typography>
                </Box>
                <Box sx={{ ...card, mb: 0, flex: 1 }}>
                    <Box sx={ibox}><Diversity3Icon sx={{ color: C.yellow, fontSize: 22 }} /></Box>
                    <Typography variant="subtitle2" sx={{ color: C.yellow, fontWeight: 700, mb: 0.5 }}>Community Focused</Typography>
                    <Typography variant="body2" sx={body}>A place where sport meets local culture and social connection.</Typography>
                </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: '20px', mb: '20px', flexDirection: { xs: 'column', sm: 'row' } }}>
                <Box sx={{ ...card, mb: 0, flex: 1 }}>
                    <Box sx={ibox}><EmojiEventsIcon sx={{ color: C.yellow, fontSize: 22 }} /></Box>
                    <Typography variant="subtitle2" sx={{ color: C.yellow, fontWeight: 700, mb: 0.5 }}>Sporting Excellence</Typography>
                    <Typography variant="body2" sx={body}>Partnered with FC Rovers to support football development and local talent.</Typography>
                </Box>
                <Box sx={{ ...card, mb: 0, flex: 1 }}>
                    <Box sx={ibox}><SelfImprovementIcon sx={{ color: C.yellow, fontSize: 22 }} /></Box>
                    <Typography variant="subtitle2" sx={{ color: C.yellow, fontWeight: 700, mb: 0.5 }}>Inclusive Environment</Typography>
                    <Typography variant="body2" sx={body}>Welcoming athletes of all ages and skill levels.</Typography>
                </Box>
            </Box>

            {/* Join Us CTA */}
            <Box sx={{ ...card, textAlign: 'center', py: 5 }}>
                <Typography variant="h6" sx={heading}>Join Us</Typography>
                <Typography variant="body2" sx={{ ...body, maxWidth: 600, mx: 'auto', mb: 3 }}>
                    Experience the best of sports and community at The Grid — where passion meets play. Whether you're hitting the court for a match, training with friends, or cheering on local football stars from FC Rovers, there's a place for you here.
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center' }}>
                    <Button variant="contained" size="large" onClick={() => navigate('/booking')} sx={primaryBtn}>
                        Book Your Court
                    </Button>
                    <Button variant="outlined" size="large" onClick={() => navigate('/contact')} sx={outlineBtn}>
                        Contact Us
                    </Button>
                </Box>
            </Box>

        </Box>
    );
};

export default AboutUsPage;
